/**
 * Token Replacement Utilities
 * Handles personalised token replacement in notification content
 */

import type { NotificationTokens } from '../types';

/**
 * Removes empty HTML container elements after token replacement
 * Handles nested empty elements and whitespace-only content
 */
function cleanEmptyHtmlContainers(html: string): string {
  if (!html) return '';

  let result = html;
  let previousResult = '';

  // Iterate until no more changes (handles nested empty elements)
  while (result !== previousResult) {
    previousResult = result;

    // Remove empty block elements: <p>, <div>, <li>, <ul>, <ol>, <span>, <strong>, <em>, <a>
    // Match elements that contain only whitespace, &nbsp;, or nothing
    result = result.replace(
      /<(p|div|li|ul|ol|span|strong|em|a|td|tr|th)(\s[^>]*)?>(\s|&nbsp;)*<\/\1>/gi,
      ''
    );

    // Remove self-closing style empty patterns like <p></p> with attributes
    result = result.replace(
      /<(p|div|li|ul|ol|span|strong|em|a|td|tr|th)(\s[^>]*)?>\s*<\/\1>/gi,
      ''
    );
  }

  // Clean up leftover whitespace between tags
  result = result.replace(/>\s+</g, '><');

  // Remove completely empty lines
  result = result.replace(/^\s*[\r\n]/gm, '');

  // Restore single space between inline elements for readability
  result = result.replace(/><(?!\/)/g, '> <').replace(/> {2,}</g, '> <');

  return result.trim();
}

/**
 * Applies token replacement using simple variable substitution
 * Supports both {{ key }} and [key] syntax
 * Automatically cleans up empty HTML containers after replacement
 */
export function applyTokens(input: string, vars: Record<string, string>): string {
  if (typeof input !== 'string' || !input) return '';

  // Replace {{ key }} syntax
  let result = input.replace(/\{\{\s*([a-zA-Z0-9._:-]+)\s*\}\}/g, (_match, key: string) => {
    const val = vars[key];
    return typeof val === 'string' ? val : '';
  });

  // Replace [key] syntax (for Drupal-style tokens)
  result = result.replace(/\[([a-zA-Z0-9._:-]+)\]/g, (_match, key: string) => {
    const val = vars[`[${key}]`] || vars[key];
    return typeof val === 'string' ? val : _match;
  });

  // Clean up empty HTML containers left after token replacement
  result = cleanEmptyHtmlContainers(result);

  return result;
}

/**
 * Converts recipient data to token variables
 * Maps common user/company fields to standardised token names
 */
export function recipientToTokenVars(recipient: Record<string, unknown>): Record<string, string> {
  const tokens: Record<string, string> = {};

  // Contact tokens
  if (recipient.firstName) tokens['[contact:first_name]'] = String(recipient.firstName);
  if (recipient.familyName) tokens['[contact:last_name]'] = String(recipient.familyName);
  if (recipient.name) tokens['[contact:full_name]'] = String(recipient.name);
  if (recipient.email) tokens['[contact:email]'] = String(recipient.email);

  // Company tokens (from nested membership object or direct properties)
  const membership = recipient.membership as Record<string, unknown> | undefined;
  if (membership?.companyName) tokens['[company:name]'] = String(membership.companyName);
  if (membership?.companyCode) tokens['[company:code]'] = String(membership.companyCode);
  if (membership?.membershipLabel)
    tokens['[company:membership_label]'] = String(membership.membershipLabel);

  // Fallback to direct properties
  if (!tokens['[company:name]'] && recipient.companyName) {
    tokens['[company:name]'] = String(recipient.companyName);
  }
  if (!tokens['[company:code]'] && recipient.companyCode) {
    tokens['[company:code]'] = String(recipient.companyCode);
  }
  if (!tokens['[company:membership_label]'] && recipient.membershipLabel) {
    tokens['[company:membership_label]'] = String(recipient.membershipLabel);
  }

  return tokens;
}

/**
 * Applies token replacement with recipient data
 * Convenience function combining recipientToTokenVars and applyTokens
 */
export function applyTokensWithRecipient(
  input: string,
  recipient: Record<string, unknown> | null | undefined
): string {
  if (!input) return '';
  if (!recipient) return input;

  const vars = recipientToTokenVars(recipient);
  return applyTokens(input, vars);
}

/**
 * Creates token variables from notification user context
 */
export function createNotificationTokens(context: {
  identity?: Record<string, unknown>;
  membership?: Record<string, unknown>;
}): NotificationTokens {
  const tokens: NotificationTokens = {};

  if (context.identity) {
    const identity = context.identity;
    if (identity.firstName) tokens['[contact:first_name]'] = String(identity.firstName);
    if (identity.familyName) tokens['[contact:last_name]'] = String(identity.familyName);
    if (identity.name) tokens['[contact:full_name]'] = String(identity.name);
    if (identity.email) tokens['[contact:email]'] = String(identity.email);
  }

  if (context.membership) {
    const membership = context.membership;
    if (membership.companyName) tokens['[company:name]'] = String(membership.companyName);
    if (membership.companyCode) tokens['[company:code]'] = String(membership.companyCode);
    if (membership.membershipLabel)
      tokens['[company:membership_label]'] = String(membership.membershipLabel);
  }

  return tokens;
}

